/**
 * SummarySection — dispatcher for the slideout's summary card.
 *
 * Per-entity-type rendering is registered on the entity type's config in
 * `entityTypeRegistry.ts` under `components.summary`. This component is a
 * thin three-line dispatcher:
 *
 *   - `summary === null`       → render nothing
 *   - `summary === undefined`  → render the generic DefaultSummary fallback
 *                                (header + `ahoy_summary` property body)
 *   - `summary === Component`  → render that component
 *
 * Adding or changing the summary card for an entity type is a one-line
 * registry edit — no code lives here.
 */

import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { COMPANY, CONTACT, DEAL } from '@/lib/crm/core/entityTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { getTypeLabel } from '@/lib/crm/utils/getTypeLabel';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';

// ============================================================================
// Default summary (used when an entity type doesn't register an override)
// ============================================================================

/**
 * Pick the heading shown above the default summary card. The three primary
 * CRM types have product-named headings ("Last interaction", etc.) that
 * predate the registry; everything else falls back to `${typeLabel} summary`.
 * Uses `getTypeLabel(object)` (not `getTypeLabelByName`) so the heading is
 * composition-aware — a note converted to a task reads "Task summary" rather
 * than "Note summary".
 */
function getSummaryHeader(object: CrmObject): string {
  const entityTypeName = object.entity_type_name;
  if (entityTypeName === CONTACT) {
    return 'Last interaction';
  }
  if (entityTypeName === COMPANY) {
    return 'Company summary';
  }
  if (entityTypeName === DEAL) {
    return 'Deal summary';
  }
  return `${getTypeLabel(object)} summary`;
}

function DefaultSummary({ object }: { object: CrmObject }) {
  const header = getSummaryHeader(object);
  const summary = getPropertyValue<string>(object.properties?.ahoy_summary);
  const { schema } = usePropertySchema(object.org_id, object.entity_type_name);
  const summarySchema = schema['ahoy_summary'];

  if (!summary) {
    return null;
  }

  return (
    <div>
      <p className='text-xs md:text-sm text-[var(--color-fg-brand-primary)] mb-1'>
        {header}
      </p>
      <PropertyValueLabel
        value={summary}
        propertyType={summarySchema}
        orgId={object.org_id}
        fontSize='sm'
      />
    </div>
  );
}

// ============================================================================
// Dispatcher
// ============================================================================

interface SummarySectionProps {
  object: CrmObject;
}

export function SummarySection({ object }: SummarySectionProps) {
  const customSummary = getEntityTypeConfig(object.entity_type_name).components
    ?.summary;
  if (customSummary === null) {
    return null;
  }
  if (customSummary) {
    const Summary = customSummary;
    return <Summary object={object} />;
  }
  return <DefaultSummary object={object} />;
}
